import {
  getCustomTypesOptions,
  getRolesOptions,
  type GetRolesResponse,
  getTeamSpacesOptions,
  isAdminRole,
  type SimpleRole,
  type TeamSpace,
} from "@prismicio/editor-fields"
import { usePrefetchQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import { env } from "../env"
import { useRoleApiRolesFlag } from "../integrations/AmplitudeExperiments"
import { getRepositoryName } from "../routing"
import { resolveRoleLegacy, useMyRolesLegacy, useProfile } from "./user"

export function useTeamSpaces() {
  const { data: teamSpaces } = useSuspenseQuery(
    getTeamSpacesOptions({
      baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
      repository: getRepositoryName(),
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    }),
  )

  const { data: customTypes } = useSuspenseQuery(
    getCustomTypesOptions({
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      baseUrl: env.PRISMIC_CUSTOM_TYPES_API_BASE_URL,
      repository: getRepositoryName(),
    }),
  )

  return useMemo(() => {
    const assignedCustomTypes = teamSpaces?.flatMap(
      (teamSpace) => teamSpace.customTypes,
    )

    // Main Space consists of all enabled custom types
    // (customType.status is a boolean field, true means enabled)
    // that are not assigned to any Team Space
    const mainSpaceCustomTypes = customTypes
      ?.filter(
        (customType) =>
          customType.status && !assignedCustomTypes?.includes(customType.id),
      )
      .map((customType) => customType.id)

    const sortedTeamSpaces = (teamSpaces ?? []).sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    return [
      {
        ...mainSpace,
        customTypes: mainSpaceCustomTypes,
      },
      ...sortedTeamSpaces,
    ]
  }, [teamSpaces, customTypes])
}

export function useTeamSpace(teamSpaceId: string = mainSpace.id) {
  const teamSpaces = useTeamSpaces()

  return teamSpaces.find((teamSpace) => teamSpace.id === teamSpaceId)
}

export const mainSpace: TeamSpace = {
  id: "",
  name: "Main Space",
  customTypes: [],
}

export function usePrefetchTeamSpacesQuery() {
  usePrefetchQuery(
    getTeamSpacesOptions({
      baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
      repository: getRepositoryName(),
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    }),
  )
}

export function useMyRoles() {
  const { userId } = useProfile()

  const areRoleApiRolesEnabled = useRoleApiRolesFlag() === "on"

  const { data: roles } = useSuspenseQuery(
    getRolesOptions({
      baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
      repository: getRepositoryName(),
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      userIds: [userId],
      fetchEnabled: areRoleApiRolesEnabled,
    }),
  )

  return roles
}

interface ResolveMyRoleArgs {
  localeId?: string
  teamSpaceId?: string
}

export function useResolveMyRole() {
  const { userId } = useProfile()
  const roles = useMyRoles()
  const repositoryRole = useMyRolesLegacy()
  const areRoleApiRolesEnabled = useRoleApiRolesFlag() === "on"

  return (args: ResolveMyRoleArgs) => {
    const { localeId, teamSpaceId } = args

    if (areRoleApiRolesEnabled) {
      return resolveRole({
        roles,
        userId,
        localeId,
        teamSpaceId,
      })
    }

    return resolveRoleLegacy({
      repositoryRole,
      localeId,
    })
  }
}

interface ResolveRoleArgs {
  roles: GetRolesResponse
  userId: string
  localeId?: string
  teamSpaceId?: string
}

export function resolveRole(args: ResolveRoleArgs): SimpleRole {
  const { roles, userId, localeId, teamSpaceId } = args

  const repositoryRole = roles.repository?.rolesPerUser?.[userId] || "Readonly"

  // admin roles
  if (isAdminRole(repositoryRole)) {
    return repositoryRole
  }

  // specific team space roles
  if (roles.teamSpace && teamSpaceId && localeId) {
    const teamSpaceRoles = roles.teamSpace.rolesPerTeamSpace[teamSpaceId]

    const rolesPerLocale = teamSpaceRoles?.rolesPerUser[userId]
      ? teamSpaceRoles?.rolesPerUser[userId]?.rolesPerLocale
      : teamSpaceRoles?.rolesPerLocale

    return rolesPerLocale?.[localeId] || "Readonly"
  }

  // main space roles
  if (roles.teamSpace && teamSpaceId === "" && localeId) {
    const teamSpaceRoles = roles.teamSpace

    const rolesPerLocale = teamSpaceRoles.rolesPerUser[userId]
      ? teamSpaceRoles.rolesPerUser[userId]?.rolesPerLocale
      : teamSpaceRoles.rolesPerLocale

    return rolesPerLocale?.[localeId] || "Readonly"
  }

  // locale roles
  if (roles.locale && localeId) {
    const rolesPerLocale = roles.locale.rolesPerUser[userId]?.rolesPerLocale

    return rolesPerLocale?.[localeId] || "Readonly"
  }

  return repositoryRole
}
